<template>
  <div class="client-charge-retry">
    <div v-if="loading" class="d-flex align-center text--secondary text-body-2">
      <v-progress-circular indeterminate size="18" width="2" class="mr-2" />
      Checking your payment options...
    </div>

    <template v-else-if="status">
      <!-- Outcome of the attempt just made -->
      <v-alert
        v-if="outcome"
        :type="outcome.type"
        dense
        text
        class="text-body-2 mb-3"
      >
        {{ outcome.text }}
      </v-alert>

      <template v-if="status.available">
        <div v-if="!confirming">
          <v-btn
            color="error"
            large
            block
            depressed
            class="py-3"
            :disabled="submitting"
            @click="confirming = true"
          >
            Retry {{ toMoney(status.amount, { currency: 'USD' }) }} with
            {{ cardLabel }}
          </v-btn>
        </div>
        <v-card v-else flat outlined class="pa-3">
          <div class="text-body-2 mb-3">
            Charge
            <strong>{{ toMoney(status.amount, { currency: 'USD' }) }}</strong>
            for {{ status.invoice.period_label }} to
            <strong>{{ cardLabel }}</strong> now?
          </div>
          <div class="d-flex ga-2">
            <v-btn
              color="error"
              depressed
              :loading="submitting"
              :disabled="submitting"
              @click="submit"
            >
              Confirm charge
            </v-btn>
            <v-btn text :disabled="submitting" @click="confirming = false">
              Cancel
            </v-btn>
          </div>
        </v-card>
      </template>

      <div
        v-else-if="status.reason !== 'nothing_to_retry'"
        class="text-body-2 text--secondary"
      >
        {{ status.message }}
        <span v-if="nextAllowedAt"> Next attempt: {{ nextAllowedAt }}.</span>
      </div>

      <div
        v-if="status.attempts && status.card"
        class="text-caption text--secondary mt-2"
      >
        Charge attempts on this card today:
        {{ Math.min(status.attempts.today, status.limits.max_per_day) }} of
        {{ status.limits.max_per_day }} &middot; on this invoice:
        {{ status.attempts.invoice_total }} of
        {{ status.limits.max_per_invoice }}
        <span v-if="lastAttempt"> &middot; last attempt {{ lastAttempt }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import axios from '@/axios/config'
import FormattersMixin from 'components/mixins/formatters-mixin'

const POLL_EVERY_MS = 2000
const POLL_FOR_MS = 90000

// A client's own retry of a declined card charge (#30848). The server decides whether a
// retry may be asked for and mints the token the charge is keyed by; this component only
// shows what it says, asks once, and waits for the outcome.
export default {
  name: 'ClientChargeRetry',
  mixins: [FormattersMixin],
  data() {
    return {
      loading: true,
      status: null,
      confirming: false,
      submitting: false,
      outcome: null,
      pollTimer: null,
      destroyed: false,
    }
  },
  computed: {
    cardLabel() {
      const card = this.status && this.status.card
      if (!card) return 'your card'
      const brand = card.brand
        ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1)
        : 'Card'
      return `${brand} •••• ${card.last4}`
    },
    nextAllowedAt() {
      const at = this.status && this.status.next_allowed_at
      return at ? this.$moment(at).format('MMM D, HH:mm') : null
    },
    lastAttempt() {
      const last =
        this.status && this.status.attempts && this.status.attempts.last
      if (!last) return null
      const when = this.$moment(last.at).format('MMM D, HH:mm')
      const labels = {
        paid: 'paid',
        declined: `declined${
          last.decline_code ? ` (${last.decline_code.replace(/_/g, ' ')})` : ''
        }`,
        failed: 'failed',
        queued: 'in progress',
        already_paid: 'already paid',
      }
      return `${when}: ${labels[last.outcome] || last.outcome}`
    },
  },
  mounted() {
    this.load()
  },
  beforeDestroy() {
    this.destroyed = true
    this.stopPolling()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/billing/retry-charge')
        this.status = data
      } catch (e) {
        this.status = null
      } finally {
        this.loading = false
      }
    },
    async submit() {
      if (!this.status || !this.status.available) return
      this.submitting = true
      this.outcome = null
      const { invoice, amount, token } = this.status
      try {
        const { data } = await axios.post('/api/billing/retry-charge', {
          invoice_id: invoice.id,
          amount,
          token,
        })
        this.confirming = false
        this.pollOutcome(data.attempt)
      } catch (error) {
        // The interceptor has already shown the server's reason as a toast.
        this.submitting = false
        this.confirming = false
        const status =
          error.response && error.response.data && error.response.data.status
        if (status) this.status = status
        else await this.load()
      }
    },
    pollOutcome(attempt) {
      const startedAt = Date.now()
      const tick = async () => {
        let entry = attempt
        try {
          const { data } = await axios.get(
            `/api/billing/retry-charge/${attempt.token}`
          )
          entry = data.attempt
        } catch (e) {
          // keep waiting on a transient error
        }
        if (this.destroyed) return
        if (entry.outcome !== 'queued' || entry.paid) {
          this.finish(entry)
        } else if (Date.now() - startedAt > POLL_FOR_MS) {
          this.finish(null)
        } else {
          this.pollTimer = setTimeout(tick, POLL_EVERY_MS)
        }
      }
      tick()
    },
    async finish(entry) {
      this.stopPolling()
      this.submitting = false
      this.outcome = this.describe(entry)
      await this.load()
      if (
        entry &&
        (entry.outcome === 'paid' ||
          entry.outcome === 'already_paid' ||
          entry.paid)
      ) {
        this.$emit('paid')
      }
    },
    describe(entry) {
      if (!entry) {
        return {
          type: 'info',
          text: 'The charge is still being processed. Please check your billing page in a few minutes.',
        }
      }
      if (entry.paid) {
        return { type: 'success', text: 'Thank you, the payment went through.' }
      }
      switch (entry.outcome) {
        case 'paid':
        case 'already_paid':
          return {
            type: 'success',
            text: 'Thank you, the payment went through.',
          }
        case 'declined':
          return {
            type: 'error',
            text: `The bank declined the charge${
              entry.decline_code
                ? ` (${entry.decline_code.replace(/_/g, ' ')})`
                : ''
            }.`,
          }
        case 'skipped':
          return {
            type: 'info',
            text: 'Another charge for this invoice was already in progress. Please check again shortly.',
          }
        default:
          return {
            type: 'error',
            text: 'We could not confirm the charge. It may still have gone through, so please check your billing page before trying again.',
          }
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
  },
}
</script>
